/**
 * Smart text matching pipeline for document suggestion editing.
 *
 * Handles mismatches between what the LLM sees (parsed text) and what's
 * in the actual DOCX XML: curly quotes, tabs/whitespace, em-dashes, etc.
 *
 * Pure functions - no DI dependencies. Used by DocxEditEngine,
 * DocxExportService, and SuggestionDocumentViewer (frontend copy).
 */

// ── Types ──────────────────────────────────────────────────────────────

export type MatchConfidence =
    | 'exact'
    | 'normalized'
    | 'fuzzy'
    | 'prefix'
    | 'none';

export interface MatchResult {
    /** Whether a match was found */
    found: boolean;
    /** The actual text from the document that matched */
    matchedText: string | null;
    /** Character offset within the haystack where the match starts */
    offset: number;
    /** How confident we are in the match */
    confidence: MatchConfidence;
    /** Which normalization step produced the match */
    matchMethod: string;
}

export interface ParagraphMatchResult {
    /** Index into the paragraph list */
    index: number;
    /** The paragraph text content */
    paragraphText: string;
    /** Match confidence */
    confidence: MatchConfidence;
    /** Which strategy found it */
    matchMethod: string;
}

export interface DidYouMeanSuggestion {
    /** The candidate text from the document */
    text: string;
    /** Where it was found (paragraph index) */
    paragraphIndex: number;
    /** Similarity score 0-1 */
    similarity: number;
}

// ── Normalization Primitives ───────────────────────────────────────────

const LEFT_SINGLE_CURLY = '\u2018';
const RIGHT_SINGLE_CURLY = '\u2019';
const LEFT_DOUBLE_CURLY = '\u201C';
const RIGHT_DOUBLE_CURLY = '\u201D';

/** Convert curly quotes to straight quotes. */
export function normalizeQuotes(s: string): string {
    return s.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'");
}

/** Collapse all whitespace (tabs, nbsp, multi-space) to single space and trim. */
export function normalizeWhitespace(s: string): string {
    return s.replace(/[\s\u00A0]+/g, ' ').trim();
}

/** Normalize punctuation: em/en-dash → hyphen, ellipsis → "...". */
export function normalizePunctuation(s: string): string {
    return s.replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...');
}

/** Apply all normalizations (quotes + whitespace + punctuation). */
export function normalizeAll(s: string): string {
    return normalizePunctuation(normalizeWhitespace(normalizeQuotes(s)));
}

// ── Offset Mapping ─────────────────────────────────────────────────────

/**
 * Build a mapping from normalized-string positions to original-string
 * positions. Used to extract the original text span after matching
 * against a normalized copy.
 *
 * Returns an array where `map[normalizedIndex] = originalIndex`.
 */
function buildOffsetMap(original: string, normalized: string): number[] {
    const map: number[] = [];
    let oi = 0;

    for (let ni = 0; ni < normalized.length; ni++) {
        // Skip original chars that were collapsed by normalization
        while (oi < original.length && map.length === ni) {
            const origChar = original[oi];
            const normChar = normalized[ni];

            // Check if this original char maps to the current normalized char
            if (origChar === normChar) {
                map.push(oi);
                oi++;
                break;
            }

            // Quote normalization: curly → straight
            if (
                (normChar === '"' &&
                    (origChar === LEFT_DOUBLE_CURLY ||
                        origChar === RIGHT_DOUBLE_CURLY)) ||
                (normChar === "'" &&
                    (origChar === LEFT_SINGLE_CURLY ||
                        origChar === RIGHT_SINGLE_CURLY))
            ) {
                map.push(oi);
                oi++;
                break;
            }

            // Punctuation normalization: em/en-dash → hyphen
            if (
                normChar === '-' &&
                (origChar === '\u2013' || origChar === '\u2014')
            ) {
                map.push(oi);
                oi++;
                break;
            }

            // Ellipsis: single char → three dots
            if (normChar === '.' && origChar === '\u2026') {
                map.push(oi);
                // The ellipsis char maps to the first '.'; next two '.' also map to it
                if (ni + 1 < normalized.length && normalized[ni + 1] === '.') {
                    ni++;
                    map.push(oi);
                }
                if (ni + 1 < normalized.length && normalized[ni + 1] === '.') {
                    ni++;
                    map.push(oi);
                }
                oi++;
                break;
            }

            // Whitespace collapse: skip extra whitespace in original
            if (normChar === ' ' && /[\s\u00A0]/.test(origChar)) {
                map.push(oi);
                oi++;
                // Skip remaining collapsed whitespace
                while (
                    oi < original.length &&
                    /[\s\u00A0]/.test(original[oi])
                ) {
                    oi++;
                }
                break;
            }

            // Trimmed leading/trailing whitespace - skip in original
            if (/[\s\u00A0]/.test(origChar)) {
                oi++;
                continue;
            }

            // Fallback: map directly
            map.push(oi);
            oi++;
            break;
        }
    }

    return map;
}

/**
 * Extract the original text span corresponding to a match found in
 * the normalized string.
 */
function extractOriginalSpan(
    original: string,
    normalized: string,
    normOffset: number,
    normLength: number,
): string {
    const map = buildOffsetMap(original, normalized);

    const origStart = map[normOffset];
    if (origStart === undefined) return original;

    // Find the end: last mapped char + 1
    const lastNormIdx = normOffset + normLength - 1;
    const origEnd = map[lastNormIdx];
    if (origEnd === undefined) return original.slice(origStart);

    // Extend to include the full original character at the end position
    // (handles multi-byte or multi-char normalizations)
    const endPos = origEnd + 1;
    // If the original char at origEnd is a multi-char normalization source
    // (e.g. ellipsis), we already point past it
    return original.slice(origStart, endPos);
}

// ── Core Matching ──────────────────────────────────────────────────────

/**
 * Find `needle` within `haystack` using a multi-step normalization pipeline.
 * Stops at the first successful match.
 */
export function findSubstring(haystack: string, needle: string): MatchResult {
    if (!needle || !haystack) {
        return {
            found: false,
            matchedText: null,
            offset: -1,
            confidence: 'none',
            matchMethod: 'empty-input',
        };
    }

    // Step 1: Exact match
    const exactIdx = haystack.indexOf(needle);
    if (exactIdx !== -1) {
        return {
            found: true,
            matchedText: needle,
            offset: exactIdx,
            confidence: 'exact',
            matchMethod: 'exact',
        };
    }

    // Step 2-4: Combined normalization (quotes + whitespace + punctuation)
    const normHay = normalizeAll(haystack);
    const normNeedle = normalizeAll(needle);

    const normIdx = normHay.indexOf(normNeedle);
    if (normIdx !== -1) {
        const matchedText = extractOriginalSpan(
            haystack,
            normHay,
            normIdx,
            normNeedle.length,
        );
        return {
            found: true,
            matchedText,
            offset: haystack.indexOf(matchedText),
            confidence: 'normalized',
            matchMethod: 'quote-whitespace-punctuation',
        };
    }

    // Step 5: Case-insensitive
    const lowerHay = normHay.toLowerCase();
    const lowerNeedle = normNeedle.toLowerCase();

    const lowerIdx = lowerHay.indexOf(lowerNeedle);
    if (lowerIdx !== -1) {
        const matchedText = extractOriginalSpan(
            haystack,
            normHay,
            lowerIdx,
            lowerNeedle.length,
        );
        return {
            found: true,
            matchedText,
            offset: haystack.indexOf(matchedText),
            confidence: 'fuzzy',
            matchMethod: 'case-insensitive',
        };
    }

    // Step 6: Prefix match (only for longer strings to avoid false positives)
    if (lowerNeedle.length > 20) {
        const prefixLen = Math.max(60, Math.floor(lowerNeedle.length * 0.4));
        const prefix = lowerNeedle.slice(
            0,
            Math.min(prefixLen, lowerNeedle.length),
        );

        const prefixIdx = lowerHay.indexOf(prefix);
        if (prefixIdx !== -1) {
            const matchedText = extractOriginalSpan(
                haystack,
                normHay,
                prefixIdx,
                prefix.length,
            );
            return {
                found: true,
                matchedText,
                offset: haystack.indexOf(matchedText),
                confidence: 'prefix',
                matchMethod: 'prefix',
            };
        }
    }

    return {
        found: false,
        matchedText: null,
        offset: -1,
        confidence: 'none',
        matchMethod: 'no-match',
    };
}

// ── Paragraph-Level Matching ───────────────────────────────────────────

/**
 * Find which paragraph contains the needle text. Uses the substring
 * matching pipeline, with proximity to `hintIndex` as tiebreaker.
 */
export function findParagraph(
    paragraphs: Array<{ index: number; text: string }>,
    needle: string,
    hintIndex: number,
): ParagraphMatchResult | null {
    if (!needle) return null;

    type Candidate = ParagraphMatchResult & { distance: number };
    const candidates: Candidate[] = [];

    for (const p of paragraphs) {
        if (!p.text) continue;
        const result = findSubstring(p.text, needle);
        if (result.found) {
            candidates.push({
                index: p.index,
                paragraphText: p.text,
                confidence: result.confidence,
                matchMethod: result.matchMethod,
                distance: Math.abs(p.index - hintIndex),
            });
        }
    }

    if (candidates.length === 0) return null;

    // Sort by confidence (stronger first), then by proximity to hintIndex
    const confidenceOrder: Record<MatchConfidence, number> = {
        exact: 0,
        normalized: 1,
        fuzzy: 2,
        prefix: 3,
        none: 4,
    };

    candidates.sort((a, b) => {
        const confDiff =
            confidenceOrder[a.confidence] - confidenceOrder[b.confidence];
        if (confDiff !== 0) return confDiff;
        return a.distance - b.distance;
    });

    const best = candidates[0];
    return {
        index: best.index,
        paragraphText: best.paragraphText,
        confidence: best.confidence,
        matchMethod: best.matchMethod,
    };
}

// ── Style Preservation ─────────────────────────────────────────────────

/**
 * When the original document text uses curly quotes but the replacement
 * text has straight quotes, convert the replacement to match.
 */
export function preserveQuoteStyle(
    originalText: string,
    replacementText: string,
): string {
    const hasDoubleCurly =
        originalText.includes(LEFT_DOUBLE_CURLY) ||
        originalText.includes(RIGHT_DOUBLE_CURLY);
    const hasSingleCurly =
        originalText.includes(LEFT_SINGLE_CURLY) ||
        originalText.includes(RIGHT_SINGLE_CURLY);

    if (!hasDoubleCurly && !hasSingleCurly) return replacementText;

    let result = replacementText;

    if (hasDoubleCurly) {
        result = applyCurlyDoubleQuotes(result);
    }
    if (hasSingleCurly) {
        result = applyCurlySingleQuotes(result);
    }

    return result;
}

function isOpeningContext(chars: string[], index: number): boolean {
    if (index === 0) return true;
    const prev = chars[index - 1];
    return (
        prev === ' ' ||
        prev === '\t' ||
        prev === '\n' ||
        prev === '\r' ||
        prev === '(' ||
        prev === '[' ||
        prev === '{' ||
        prev === '\u2014' ||
        prev === '\u2013'
    );
}

function applyCurlyDoubleQuotes(s: string): string {
    const chars = [...s];
    const result: string[] = [];
    for (let i = 0; i < chars.length; i++) {
        if (chars[i] === '"') {
            result.push(
                isOpeningContext(chars, i)
                    ? LEFT_DOUBLE_CURLY
                    : RIGHT_DOUBLE_CURLY,
            );
        } else {
            result.push(chars[i]);
        }
    }
    return result.join('');
}

function applyCurlySingleQuotes(s: string): string {
    const chars = [...s];
    const result: string[] = [];
    for (let i = 0; i < chars.length; i++) {
        if (chars[i] === "'") {
            const prev = i > 0 ? chars[i - 1] : undefined;
            const next = i < chars.length - 1 ? chars[i + 1] : undefined;
            const prevIsLetter = prev !== undefined && /\p{L}/u.test(prev);
            const nextIsLetter = next !== undefined && /\p{L}/u.test(next);
            if (prevIsLetter && nextIsLetter) {
                // Apostrophe in contraction (don't, it's)
                result.push(RIGHT_SINGLE_CURLY);
            } else {
                result.push(
                    isOpeningContext(chars, i)
                        ? LEFT_SINGLE_CURLY
                        : RIGHT_SINGLE_CURLY,
                );
            }
        } else {
            result.push(chars[i]);
        }
    }
    return result.join('');
}

// ── Did-You-Mean Suggestions ───────────────────────────────────────────

/**
 * Find paragraphs with text similar to `needle` using longest common
 * substring ratio. Returns top candidates sorted by similarity.
 */
export function findSimilarTexts(
    paragraphs: Array<{ index: number; text: string }>,
    needle: string,
    maxResults = 3,
): DidYouMeanSuggestion[] {
    if (!needle) return [];

    const normNeedle = normalizeAll(needle).toLowerCase();
    const suggestions: DidYouMeanSuggestion[] = [];

    for (const p of paragraphs) {
        if (!p.text) continue;
        const normPara = normalizeAll(p.text).toLowerCase();
        const lcsLen = longestCommonSubstring(normNeedle, normPara);
        const similarity = (2 * lcsLen) / (normNeedle.length + normPara.length);

        if (similarity > 0.3) {
            suggestions.push({
                text: p.text.slice(0, 120),
                paragraphIndex: p.index,
                similarity,
            });
        }
    }

    suggestions.sort((a, b) => b.similarity - a.similarity);
    return suggestions.slice(0, maxResults);
}

/** Length of the longest common substring between two strings. */
function longestCommonSubstring(a: string, b: string): number {
    // Optimize: use shorter string as rows
    if (a.length > b.length) [a, b] = [b, a];
    const m = a.length;
    const n = b.length;

    // Use single row DP to save memory
    let prev = new Uint16Array(n + 1);
    let curr = new Uint16Array(n + 1);
    let maxLen = 0;

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (a[i - 1] === b[j - 1]) {
                curr[j] = prev[j - 1] + 1;
                if (curr[j] > maxLen) maxLen = curr[j];
            } else {
                curr[j] = 0;
            }
        }
        [prev, curr] = [curr, prev];
        curr.fill(0);
    }

    return maxLen;
}
